/**
 * KOPS-545: Sponsor Partnership Landing Page
 *
 * Route: /partners
 *
 * Public-facing page that drives sponsors to signup.
 * Hooks with the problem, shows the value, one CTA.
 * Does NOT disclose pricing or technical details.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine, faUsers, faRobot, faShieldAlt, faArrowRight,
  faCheckCircle, faExclamationTriangle, faHandshake, faEye, faLock
} from '@fortawesome/free-solid-svg-icons';
import { colors, fonts, FONT_LINK_HREF } from '../broker-intelligence/shared/colors';

const SponsorLandingPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div style={{ background: '#fff', minHeight: '100vh', fontFamily: fonts.body }}>
      <link href={FONT_LINK_HREF} rel="stylesheet" />

      {/* Nav */}
      <nav style={{ background: colors.navy, padding: '16px 0', borderBottom: `2px solid ${colors.gold}` }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <a href="/" style={{ textDecoration: 'none' }}>
            <span style={{ fontFamily: fonts.display, fontSize: 24, letterSpacing: 2, color: colors.gold }}>
              AI<span style={{ color: '#fff' }}>Search</span>Portal
            </span>
          </a>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <a href="/pricing" style={{ color: '#8FA3BF', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>Pricing</a>
            <a href="/sign-in" style={{ color: '#8FA3BF', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>Sign In</a>
            <button onClick={() => navigate('/sponsor/signup')}
              style={{ background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, border: 'none', borderRadius: 6, padding: '8px 20px',
                color: colors.navy, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ background: `linear-gradient(135deg, ${colors.navy} 0%, #1a2d4a 100%)`, padding: '80px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div style={{ display: 'inline-block', background: 'rgba(201,149,42,0.15)', borderRadius: 20, padding: '6px 16px', marginBottom: 24 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: colors.gold, letterSpacing: 1, textTransform: 'uppercase' }}>Sponsor Partnership Program</span>
          </div>
          <h1 style={{ fontFamily: fonts.display, fontSize: 48, color: '#fff', margin: '0 0 16px', lineHeight: 1.1, letterSpacing: 1 }}>
            Your Agents Lost <span style={{ color: colors.gold }}>$168K</span> Last Year.
          </h1>
          <h2 style={{ fontFamily: fonts.display, fontSize: 28, color: '#8FA3BF', margin: '0 0 24px', fontWeight: 400, letterSpacing: 1 }}>
            We Have the Data. Now You Can Coach Them Back.
          </h2>
          <p style={{ fontSize: 17, color: '#8FA3BF', lineHeight: 1.7, maxWidth: 600, margin: '0 auto 32px' }}>
            Give your agent network AI-powered tools they'd pay $185/month for — and get
            coaching intelligence that transforms your relationship from transactional to strategic.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/sponsor/signup')}
              style={{ background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, border: 'none', borderRadius: 8, padding: '16px 40px',
                fontSize: 16, fontWeight: 700, color: colors.navy, cursor: 'pointer' }}>
              Start Your Partnership <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 8 }} />
            </button>
            <button onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              style={{ background: 'transparent', border: `2px solid ${colors.gold}`, borderRadius: 8, padding: '16px 32px',
                fontSize: 16, fontWeight: 700, color: colors.gold, cursor: 'pointer' }}>
              See How It Works
            </button>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 36, color: colors.navy, textAlign: 'center', margin: '0 0 16px', letterSpacing: 1 }}>
            THE RELATIONSHIP IS BROKEN
          </h2>
          <p style={{ textAlign: 'center', fontSize: 15, color: '#666', maxWidth: 650, margin: '0 auto 48px' }}>
            Rate sheets and lunch meetings aren't enough anymore. Your agents have options — and they're quietly moving to whoever adds the most value.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 24 }}>
            {[
              { icon: faExclamationTriangle, color: '#e74c3c', stat: '65%', text: 'of past buyers use a different agent when they sell. Your agents are losing repeat business they don\'t even know about.' },
              { icon: faEye, color: '#e67e22', stat: '0%', text: 'visibility into agent production. You sponsor tools but have no idea if they\'re growing, stalling, or losing clients to competitors.' },
              { icon: faHandshake, color: '#3498db', stat: '1 bad call', text: 'is all it takes. One slow response, one missed follow-up — and the agent quietly moves their next deal to a competitor.' },
            ].map((item, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 12, padding: 28, borderTop: `3px solid ${item.color}` }}>
                <FontAwesomeIcon icon={item.icon} style={{ fontSize: 24, color: item.color, marginBottom: 12 }} />
                <div style={{ fontFamily: fonts.display, fontSize: 36, color: colors.navy, letterSpacing: 1, marginBottom: 8 }}>{item.stat}</div>
                <p style={{ fontSize: 14, color: '#555', lineHeight: 1.6, margin: 0 }}>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 36, color: colors.navy, textAlign: 'center', margin: '0 0 16px', letterSpacing: 1 }}>
            HOW IT WORKS
          </h2>
          <p style={{ textAlign: 'center', fontSize: 15, color: '#666', maxWidth: 600, margin: '0 auto 48px' }}>
            Three things happen the day you sign up.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
            {[
              { step: '01', title: 'You Purchase Seats', desc: 'Choose how many agent seats you need. Each seat gives one agent full access to AI Pro — the complete AI-powered real estate platform.', icon: faUsers },
              { step: '02', title: 'Agents Get AI Tools', desc: 'Invite agents by email. They get a 14-day free trial with AI-powered CRM, smart CMA reports, lead coaching, property intelligence, and daily alerts.', icon: faRobot },
              { step: '03', title: 'You Get Coaching Intelligence', desc: 'Your Sponsor Dashboard shows every agent\'s production history, retention risks, and AI-generated coaching recommendations backed by MLS data.', icon: faChartLine },
            ].map((item, i) => (
              <div key={i} style={{ background: '#f8f9fa', borderRadius: 12, padding: 28 }}>
                <div style={{ fontFamily: fonts.display, fontSize: 48, color: colors.gold, opacity: 0.3, marginBottom: 8 }}>{item.step}</div>
                <FontAwesomeIcon icon={item.icon} style={{ fontSize: 20, color: colors.gold, marginBottom: 12 }} />
                <h3 style={{ fontSize: 18, fontWeight: 700, color: colors.navy, margin: '0 0 8px' }}>{item.title}</h3>
                <p style={{ fontSize: 14, color: '#555', lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You See */}
      <section style={{ padding: '80px 24px', background: colors.navy }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 36, color: '#fff', textAlign: 'center', margin: '0 0 16px', letterSpacing: 1 }}>
            YOUR COACHING CONSOLE
          </h2>
          <p style={{ textAlign: 'center', fontSize: 15, color: '#8FA3BF', maxWidth: 600, margin: '0 auto 48px' }}>
            Stop guessing. Start coaching with data.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {[
              { title: 'MLS Production History', desc: 'Every closed deal — buyer side, listing side, volume, year-over-year trends. See who\'s growing and who\'s stalling.' },
              { title: 'Client Retention Analysis', desc: 'Which past buyers listed with a different agent? How much commission was lost? Name the competitor who took the listing.' },
              { title: 'AI Coaching Recommendations', desc: 'Claude AI generates data-backed coaching prompts for each 1:1 meeting. Focus on what matters — retention, listings, growth.' },
              { title: 'AI Strategy Chat', desc: 'Ask questions about any agent\'s business in real-time. "How should I approach Sarah about her listing numbers?" — answered with data.' },
              { title: 'Predictive Sell Scores', desc: 'Which properties in the agent\'s database are most likely to sell next? Scored by ownership duration, equity, triggers.' },
              { title: 'Property Alert Triggers', desc: 'Foreclosures, death deeds, absentee owners — time-sensitive opportunities surfaced automatically from county records.' },
            ].map((item, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: 20, borderLeft: `3px solid ${colors.gold}` }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: colors.gold, margin: '0 0 6px' }}>{item.title}</h4>
                <p style={{ fontSize: 13, color: '#8FA3BF', lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 36, color: colors.navy, textAlign: 'center', margin: '0 0 48px', letterSpacing: 1 }}>
            BUILT FOR
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
            {[
              { title: 'Lenders', desc: 'Turn loan officers into business advisors. Coach agents on production, not just rate sheets.' },
              { title: 'Title Companies', desc: 'Deepen agent relationships beyond the closing table. Add value they can\'t get anywhere else.' },
              { title: 'Brokerages', desc: 'Give your agents enterprise tools at scale. Monitor production and retention across your roster.' },
              { title: 'Coaches & Trainers', desc: 'Data-backed coaching sessions. AI-generated prompts for every 1:1. Track progress over time.' },
            ].map((item, i) => (
              <div key={i} style={{ textAlign: 'center', padding: 24 }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                  <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: 24, color: colors.navy }} />
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: '0 0 6px' }}>{item.title}</h3>
                <p style={{ fontSize: 13, color: '#666', lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section style={{ padding: '60px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: 32, color: colors.gold, marginBottom: 16 }} />
          <h2 style={{ fontFamily: fonts.display, fontSize: 28, color: colors.navy, margin: '0 0 16px', letterSpacing: 1 }}>
            RESPA COMPLIANT BY DESIGN
          </h2>
          <p style={{ fontSize: 14, color: '#555', lineHeight: 1.8, maxWidth: 550, margin: '0 auto 16px' }}>
            Software access only. No pass-through payments. No referral obligations.
            Every agent acknowledges the four RESPA pillars before activation.
            Full compliance records available on your dashboard.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            <FontAwesomeIcon icon={faLock} style={{ color: colors.gold }} />
            <span style={{ fontSize: 13, color: '#888' }}>Agent data privacy protected</span>
            <span style={{ color: '#ddd' }}>|</span>
            <span style={{ fontSize: 13, color: '#888' }}>No client PII shared with sponsors</span>
            <span style={{ color: '#ddd' }}>|</span>
            <span style={{ fontSize: 13, color: '#888' }}>MLS public records only</span>
          </div>
        </div>
      </section>

      {/* The Math */}
      <section style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 36, color: colors.navy, margin: '0 0 16px', letterSpacing: 1 }}>
            THE ROI
          </h2>
          <p style={{ fontSize: 15, color: '#666', marginBottom: 32 }}>
            One retained listing pays for the entire annual seat cost — nearly 8 times over.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            <div style={{ background: colors.navy, borderRadius: 16, padding: 32, textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: colors.gold, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Retention</div>
              <div style={{ fontFamily: fonts.display, fontSize: 40, color: '#fff', letterSpacing: 1 }}>65%</div>
              <div style={{ fontSize: 13, color: '#8FA3BF', marginTop: 8, lineHeight: 1.5 }}>of past buyers use a different agent. Coaching intelligence helps agents retain clients they'd otherwise lose.</div>
            </div>
            <div style={{ background: colors.navy, borderRadius: 16, padding: 32, textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: colors.gold, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Referrals</div>
              <div style={{ fontFamily: fonts.display, fontSize: 40, color: '#fff', letterSpacing: 1 }}>8X</div>
              <div style={{ fontSize: 13, color: '#8FA3BF', marginTop: 8, lineHeight: 1.5 }}>One retained client refers family, friends, and colleagues. A single save creates a referral network worth 8x the seat cost.</div>
            </div>
            <div style={{ background: colors.navy, borderRadius: 16, padding: 32, textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: colors.gold, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Loyalty</div>
              <div style={{ fontFamily: fonts.display, fontSize: 40, color: '#fff', letterSpacing: 1 }}>YOU</div>
              <div style={{ fontSize: 13, color: '#8FA3BF', marginTop: 8, lineHeight: 1.5 }}>become the partner who helped them grow — not just another vendor. Agents don't leave sponsors who make them money.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '80px 24px', background: `linear-gradient(135deg, ${colors.navy} 0%, #1a2d4a 100%)`, textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 36, color: '#fff', margin: '0 0 16px', letterSpacing: 1 }}>
            READY TO SEE THE DATA?
          </h2>
          <p style={{ fontSize: 16, color: '#8FA3BF', marginBottom: 32 }}>
            Sign up today. Invite your agents. Start coaching with intelligence — not intuition.
          </p>
          <button onClick={() => navigate('/sponsor/signup')}
            style={{ background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, border: 'none', borderRadius: 8, padding: '18px 48px',
              fontSize: 18, fontWeight: 700, color: colors.navy, cursor: 'pointer', marginBottom: 16 }}>
            Start Your Partnership <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 8 }} />
          </button>
          <p style={{ fontSize: 13, color: '#8FA3BF' }}>
            Questions? <a href="mailto:michael@kairosiq.ai" style={{ color: colors.gold }}>michael@kairosiq.ai</a>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: colors.navy, borderTop: `1px solid rgba(201,149,42,0.2)`, padding: '24px', textAlign: 'center' }}>
        <p style={{ color: '#8FA3BF', fontSize: 11, margin: 0 }}>
          KairosIQ AI, Inc. | 2700 S Gilbert Rd Ste 5, Chandler, AZ 85286 |{' '}
          <a href="https://aisearchportal.com" style={{ color: colors.gold, textDecoration: 'none' }}>aisearchportal.com</a>
        </p>
      </footer>
    </div>
  );
};

export default SponsorLandingPage;
